import { useReactiveVar } from '@apollo/client';
import { Close, SettingsBackupRestore, ZoomIn, ZoomOut } from '@mui/icons-material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Dialog, IconButton, Skeleton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import AUDIO_IMAGE from 'src/assets/audio-image.png';
import PDFPreview from 'src/components/PDF/PDFPreview';
import VideoJS from 'src/components/VideoJs';
import {
  AUDIO,
  IMAGE,
  MEDIA_PATH,
  PROXY_FORMAT_SLUG,
  SAMPLE_DOWNLOAD_LINK,
  THUMBNAIL_FORMAT_SLUG,
  VIDEO,
} from 'src/constants';
import { determineMediaType } from 'src/helpers';
import { getFormat, getFormatId } from 'src/helpers/formats';
import { isVisualMode } from 'src/helpers/mode';
import viewStateVar from 'src/store/viewState';
import type Player from 'video.js/dist/types/player';

const MIN_SCALE = 0.2;
const MAX_SCALE = 8;
const INITIAL_SCALE = 1;

interface FormatNode {
  id: string;
  online?: boolean;
  mimetype?: string;
}

interface FormatEdge {
  node: FormatNode;
}

interface FormatSet {
  edges: FormatEdge[];
}

interface ContentObject {
  id: string;
  name?: string;
  formatSet: FormatSet;
  inpoint?: string;
  timecode?: string;
  mediatype?: string;
}

interface CollectionItemNode {
  contentObject: ContentObject;
}

interface CollectionItemEdge {
  node: CollectionItemNode;
  thumbnail?: {
    id: string;
  };
}

interface CollectionData {
  collection?: {
    items?: {
      edges: CollectionItemEdge[];
    };
  };
}

interface ProxyFormatObject {
  node?: FormatNode;
}

interface CollectionPreviewModalProps {
  collectionData: CollectionData;
}

interface ImagePreviewProps {
  src: string;
}

interface VideoPreviewProps {
  thumbnailId: string | undefined;
  asset: CollectionItemEdge | undefined;
  proxyFmtObj: ProxyFormatObject | undefined;
  videoProxyId: string | undefined;
}

interface TransformState {
  scale: number;
  positionX: number;
  positionY: number;
}

const useStyles = makeStyles((_theme: Theme) => ({
  closeIcon: {
    position: 'fixed',
    top: 10,
    right: 10,
    zIndex: 12002,
  },
  nextIcon: {
    position: 'fixed',
    top: '50%',
    right: 10,
    zIndex: 12002,
  },
  prevIcon: {
    position: 'fixed',
    top: '50%',
    left: 10,
    zIndex: 12002,
  },
  videoModal: {
    width: '70%',
    height: '100%',
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
}));

const useImagePreviewStyles = makeStyles((_theme: Theme) => ({
  imagesContainer: {
    margin: 'auto',
    position: 'relative',
    paddingTop: '56.25%',
  },
  boxImages: {
    objectFit: 'contain',
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
  },
  modalImage: {
    width: '100%',
    height: '100%',
    maxWidth: '100vw',
    maxHeight: '90vh',
  },
  closeIcon: {
    position: 'fixed',
    top: 10,
    right: 10,
    zIndex: 12002,
  },
  mainSection: {
    display: 'flex',
    justifyContent: 'center',
  },
  tools: {
    opacity: 0.5,
    position: 'absolute',
    bottom: 20,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'center',
    '&:hover': {
      opacity: 1,
    },
  },
}));

const ImagePreview: React.FC<ImagePreviewProps> = ({ src }) => {
  const classes = useImagePreviewStyles();
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

  const handleImageLoad = (): void => {
    setIsImageLoaded(true);
  };

  return (
    <TransformWrapper initialScale={INITIAL_SCALE} minScale={MIN_SCALE} centerOnInit={true} centerZoomedOut={false}>
      {({
        zoomIn,
        zoomOut,
        resetTransform,
        state,
      }: {
        zoomIn: () => void;
        zoomOut: () => void;
        resetTransform: () => void;
        state: TransformState;
      }) => {
        return (
          <div className={classes.mainSection}>
            <div className={classes.tools}>
              <IconButton disabled={state.scale === MAX_SCALE} onClick={() => zoomIn()} size="large">
                <ZoomIn />
              </IconButton>
              <IconButton disabled={state.scale === MIN_SCALE} onClick={() => zoomOut()} size="large">
                <ZoomOut />
              </IconButton>
              <IconButton disabled={state.scale === INITIAL_SCALE} onClick={() => resetTransform()} size="large">
                <SettingsBackupRestore />
              </IconButton>
            </div>
            <TransformComponent
              contentStyle={{
                width: '100%',
                height: '100%',
                margin: 'auto',
              }}
              wrapperStyle={{
                maxWidth: '100vw',
                maxHeight: '90vh',
              }}
            >
              {!isImageLoaded && (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    width: 400,
                    height: 300,
                    bgcolor: (theme) => theme.palette.action.disabledBackground,
                  }}
                />
              )}
              <img src={src} onLoad={handleImageLoad} className={classes.modalImage} alt="Preview" />
            </TransformComponent>
          </div>
        );
      }}
    </TransformWrapper>
  );
};

const VideoPreview: React.FC<VideoPreviewProps> = ({ thumbnailId, asset, proxyFmtObj, videoProxyId }) => {
  const playerRef = useRef<Player | null>(null);
  const classes = useStyles();
  const thumbnailFormat = `${MEDIA_PATH}/${thumbnailId}`;
  const audioPoster = thumbnailFormat ? thumbnailFormat : AUDIO_IMAGE;
  const mediaPoster = determineMediaType(proxyFmtObj) === AUDIO ? audioPoster : thumbnailFormat;
  const viewState = useReactiveVar(viewStateVar);

  const setPlayer = (_player: Player): void => {
    playerRef.current = _player;
  };

  return (
    <div className={classes.videoModal}>
      <VideoJS
        enableKeyBindings={isVisualMode(viewState.mode)}
        asset={asset?.node.contentObject}
        setPlayer={setPlayer}
        audioPosterMode={determineMediaType(proxyFmtObj) === AUDIO}
        videoUrl={`${MEDIA_PATH}/${videoProxyId}`}
        poster={mediaPoster}
      />
    </div>
  );
};

const CollectionPreviewModal: React.FC<CollectionPreviewModalProps> = ({ collectionData }) => {
  const { collectionPreviewIndex } = viewStateVar();
  const asset =
    collectionPreviewIndex !== undefined ? collectionData?.collection?.items?.edges[collectionPreviewIndex] : undefined;
  const formatSet = asset?.node?.contentObject.formatSet;
  const numberOfCollectionItems = collectionData?.collection?.items?.edges.length || 0;
  const proxyFmtObj = getFormat(
    asset?.node?.contentObject.formatSet as unknown as
      | { edges?: Array<{ node: { id: string; fmtType: { slug: string }; online?: boolean } }> }
      | undefined,
    PROXY_FORMAT_SLUG,
  ) as ProxyFormatObject | undefined;
  const imageProxyId = getFormatId(
    asset?.node?.contentObject.formatSet.edges as unknown as
      | Array<{ node: { id: string; fmtType: { slug: string } } }>
      | undefined,
    PROXY_FORMAT_SLUG,
  );
  const thumbnailId = getFormatId(
    formatSet?.edges as unknown as Array<{ node: { id: string; fmtType: { slug: string } } }> | undefined,
    THUMBNAIL_FORMAT_SLUG,
  );
  const videoProxyId = getFormatId(
    asset?.node.contentObject.formatSet.edges as unknown as
      | Array<{ node: { id: string; fmtType: { slug: string } } }>
      | undefined,
    PROXY_FORMAT_SLUG,
  );

  const [disabledNextButton, setDisabledNextButton] = useState<boolean>(false);
  const [disabledPrevButton, setDisabledPrevButton] = useState<boolean>(false);

  const classes = useStyles();

  const onClose = (): void => {
    viewStateVar({
      ...viewStateVar(),
      collectionPreviewIndex: undefined,
    });
  };

  const goNext = (): void => {
    if (collectionPreviewIndex !== undefined) {
      viewStateVar({
        ...viewStateVar(),
        collectionPreviewIndex: collectionPreviewIndex + 1,
      });
    }
  };

  const goPrev = (): void => {
    if (collectionPreviewIndex !== undefined) {
      viewStateVar({
        ...viewStateVar(),
        collectionPreviewIndex: collectionPreviewIndex - 1,
      });
    }
  };

  const keyDownHandler = (k: React.KeyboardEvent<HTMLDivElement>): void => {
    if (k.keyCode === 37 && disabledPrevButton === false) {
      goPrev();
    }
    if (k.keyCode === 39 && disabledNextButton === false) {
      goNext();
    }
  };

  useEffect(() => {
    if (collectionPreviewIndex === 0) {
      setDisabledPrevButton(true);
    } else {
      setDisabledPrevButton(false);
    }
    if (collectionPreviewIndex === numberOfCollectionItems - 1) {
      setDisabledNextButton(true);
    } else {
      setDisabledNextButton(false);
    }
  }, [collectionPreviewIndex, numberOfCollectionItems]);

  if (collectionPreviewIndex === undefined) return null;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          overflow: 'hidden',
          boxShadow: 'none',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.92)' : 'rgba(0, 0, 0, 0.85)'),
          },
        },
      }}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: keyboard navigation wrapper for modal, interactive elements inside have proper accessibility */}
      <div onKeyDown={keyDownHandler} style={{ width: '100%', height: '100%' }}>
        <IconButton
          className={classes.closeIcon}
          onClick={onClose}
          size="large"
          sx={{ color: (theme) => theme.palette.common.white }}
        >
          <Close />
        </IconButton>
        <IconButton
          className={classes.nextIcon}
          onClick={goNext}
          disabled={disabledNextButton}
          size="large"
          sx={{ color: (theme) => theme.palette.common.white }}
        >
          <NavigateNextIcon />
        </IconButton>
        <IconButton
          className={classes.prevIcon}
          onClick={goPrev}
          disabled={disabledPrevButton}
          size="large"
          sx={{ color: (theme) => theme.palette.common.white }}
        >
          <NavigateBeforeIcon />
        </IconButton>
        {determineMediaType(proxyFmtObj) === VIDEO || determineMediaType(proxyFmtObj) === AUDIO ? (
          <VideoPreview
            asset={asset}
            proxyFmtObj={proxyFmtObj}
            thumbnailId={thumbnailId ?? undefined}
            videoProxyId={videoProxyId ?? undefined}
          />
        ) : determineMediaType(proxyFmtObj) === IMAGE ? (
          <ImagePreview src={`${MEDIA_PATH}/${imageProxyId}`} />
        ) : (
          <PDFPreview
            pdfUrl={`${MEDIA_PATH}/${imageProxyId}`}
            downloadLink={SAMPLE_DOWNLOAD_LINK}
            isOnMediaPage={false}
          />
        )}
      </div>
    </Dialog>
  );
};

export default CollectionPreviewModal;
